import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { setApplyBrandingCallback } from './AuthContext';

interface AssociationBranding {
  name?: string;
  logo_url?: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  text_color: string;
  background_color: string;
}

interface PrimaryColorContextType {
  primaryColor: string;
  setPrimaryColor: (color: string) => Promise<void>;
  loading: boolean;
  PRIMARY_COLORS: typeof PRIMARY_COLORS;
  associationBranding: AssociationBranding | null;
  applyAssociationBranding: (branding: AssociationBranding) => void;
}

// Colori predefiniti con i loro valori HSL
export const PRIMARY_COLORS = [
  { name: 'Verde', value: '#16a34a', hsl: '142 76% 36%' },
  { name: 'Blu', value: '#2563eb', hsl: '217 91% 53%' },
  { name: 'Viola', value: '#7c3aed', hsl: '263 70% 58%' },
  { name: 'Rosa', value: '#db2777', hsl: '330 81% 51%' },
  { name: 'Arancione', value: '#ea580c', hsl: '21 90% 48%' },
  { name: 'Rosso', value: '#dc2626', hsl: '0 72% 51%' },
  { name: 'Ciano', value: '#0891b2', hsl: '189 94% 37%' },
  { name: 'Indaco', value: '#4f46e5', hsl: '239 84% 67%' },
] as const;

const PrimaryColorContext = createContext<PrimaryColorContextType | null>(null);

// Converte HEX a HSL
const hexToHsl = (hex: string): string => {
  if (!hex || !hex.startsWith('#')) return '142 76% 36%';
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface PrimaryColorProviderProps {
  children: ReactNode;
}

export const PrimaryColorProvider: React.FC<PrimaryColorProviderProps> = ({ children }) => {
  const [primaryColor, setPrimaryColor] = useState(() => {
    return localStorage.getItem('horecaplat_primary_color') || '#16a34a';
  });
  const [loading, setLoading] = useState(true);
  const [associationBranding, setAssociationBranding] = useState<AssociationBranding | null>(null);

  // Applica branding dell'associazione
  const applyAssociationBranding = (branding: AssociationBranding) => {
    setAssociationBranding(branding);
    
    const root = document.documentElement;
    
    // Applica colore primario
    if (branding.primary_color) {
      const primaryHsl = hexToHsl(branding.primary_color);
      root.style.setProperty('--primary', primaryHsl);
      setPrimaryColor(branding.primary_color);
    }
    
    // Applica colore secondario (usato per hover states)
    if (branding.secondary_color) {
      const secondaryHsl = hexToHsl(branding.secondary_color);
      root.style.setProperty('--secondary', secondaryHsl);
    }
    
    // Applica colore accento
    if (branding.accent_color) {
      const accentHsl = hexToHsl(branding.accent_color);
      root.style.setProperty('--accent', accentHsl);
    }
    
    // Salva nel localStorage per persistenza
    localStorage.setItem('horecaplat_association_branding', JSON.stringify(branding));
  };

  // Registra callback per AuthContext
  useEffect(() => {
    setApplyBrandingCallback(applyAssociationBranding);
  }, []);

  // Carica colore dal server all'avvio
  useEffect(() => {
    const loadUserColor = async () => {
      try {
        const token = localStorage.getItem('horecaplat_token');
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const res = await axios.get(`${API}/auth/me`);
          
          // Se l'utente appartiene a un'associazione, applica il branding
          if (res.data.association_branding) {
            applyAssociationBranding(res.data.association_branding);
          } else {
            // Altrimenti carica le preferenze personali
            try {
              const prefRes = await axios.get(`${API}/users/preferences`);
              if (prefRes.data.primary_color) {
                setPrimaryColor(prefRes.data.primary_color);
                localStorage.setItem('horecaplat_primary_color', prefRes.data.primary_color);
              }
            } catch {
              // Usa default
            }
          }
        } else {
          // Nessun token, prova a caricare branding salvato
          const savedBranding = localStorage.getItem('horecaplat_association_branding');
          if (savedBranding) {
            try {
              const branding = JSON.parse(savedBranding);
              applyAssociationBranding(branding);
            } catch {
              // Ignora errori parsing
            }
          }
        }
      } catch (error) {
        // Usa il valore dal localStorage
      } finally {
        setLoading(false);
      }
    };
    loadUserColor();
  }, []);

  // Applica il colore alle CSS variables (per utenti senza associazione)
  useEffect(() => {
    if (!associationBranding) {
      const root = document.documentElement;
      const hsl = hexToHsl(primaryColor);
      root.style.setProperty('--primary', hsl);
      localStorage.setItem('horecaplat_primary_color', primaryColor);
    }
  }, [primaryColor, associationBranding]);

  // Cambia colore e salva sul server
  const changePrimaryColor = async (newColor: string) => {
    setPrimaryColor(newColor);
    
    try {
      await axios.put(`${API}/users/preferences`, {
        primary_color: newColor
      });
    } catch (error) {
      console.error('Errore salvataggio colore:', error);
    }
  };

  return (
    <PrimaryColorContext.Provider value={{ 
      primaryColor, 
      setPrimaryColor: changePrimaryColor,
      loading,
      PRIMARY_COLORS,
      associationBranding,
      applyAssociationBranding
    }}>
      {children}
    </PrimaryColorContext.Provider>
  );
};

export const usePrimaryColor = () => {
  const context = useContext(PrimaryColorContext);
  if (!context) {
    throw new Error('usePrimaryColor must be used within PrimaryColorProvider');
  }
  return context;
};
